import { useCallback, useEffect, useMemo, useState } from "react";
import { TopBar } from "../components/TopBar";
import { Combobox } from "../components/Combobox";
import { COUNTRIES, flagEmoji, countryName } from "../lib/countries";
import { api } from "../lib/api";
import { fmtCurrency } from "../lib/format";
import { cn } from "../lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus, Loader2, Trash2, History, Search, TrendingUp, TrendingDown, Clock, Filter, Download, RotateCcw, Copy,
} from "lucide-react";
import { toast } from "sonner";

/* ISOLATED MODULE: Price List / Rate Card (unified OFFERS) + Funnel finder.
   An offer is identified by Source + Geo + Funnel (multiple offers may share a Source+Geo).
   Buying CPL = cpa_buy*(crg_buy/100); Selling CPL = cpa_sell*(crg_sell/100); NET = Sell CPL - Buy CPL.
   Sell auto-derives from buy (+$50 CPA / +1pp CRG, SRC & Funnel copied); manual edits are preserved.
   Fully separate from accounting: nothing here touches balances. */

const inputClass =
  "h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50";

const Cell = ({ value, onCommit, numeric = false, className, testId, placeholder = "", listId }) => {
  const [v, setV] = useState(value ?? (numeric ? 0 : ""));
  useEffect(() => { setV(value ?? (numeric ? 0 : "")); }, [value, numeric]);
  return (
    <input
      value={v}
      placeholder={placeholder}
      data-testid={testId}
      list={listId}
      autoComplete="off"
      className={cn(inputClass, numeric && "text-right font-mono tabular-nums", className)}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        const nv = numeric ? (v === "" || isNaN(Number(v)) ? 0 : Number(v)) : String(v);
        const old = value ?? (numeric ? 0 : "");
        if (nv !== old) onCommit(nv);
        else setV(old);
      }}
      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
    />
  );
};

const SellField = ({ value, isAuto, numeric = false, placeholder = "", listId, testId, onCommit, onReset }) => (
  <div className="flex items-center gap-1">
    <Cell value={value} numeric={numeric} placeholder={placeholder} listId={listId} testId={testId}
      className={cn(isAuto && "italic text-muted-foreground/80")} onCommit={onCommit} />
    {isAuto ? (
      <span data-testid={`${testId}-auto`} title="Auto — derived from the Buy side. Type to override."
        className="shrink-0 rounded bg-muted px-1 text-[9px] font-bold uppercase leading-4 text-muted-foreground">A</span>
    ) : (
      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-primary"
        title="Reset to auto (re-derive from Buy)" data-testid={`${testId}-reset`} onClick={onReset}>
        <RotateCcw className="h-3 w-3" />
      </Button>
    )}
  </div>
);

const ComputedCell = ({ value, testId }) => (
  <span data-testid={testId} className="font-mono text-sm tabular-nums text-muted-foreground">{fmtCurrency(value || 0)}</span>
);

const NetCell = ({ value, testId }) => {
  const v = value || 0;
  return (
    <span data-testid={testId} className={cn("inline-flex items-center justify-end gap-1 font-mono text-sm font-semibold tabular-nums",
      v > 0 ? "text-emerald-600" : v < 0 ? "text-red-600" : "text-muted-foreground")}>
      {v > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : v < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : null}
      {fmtCurrency(v)}
    </span>
  );
};

const EditRow = ({ label, children }) => (
  <div className="flex items-center gap-3">
    <span className="w-36 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
    <div className="flex-1">{children}</div>
  </div>
);

const actionLabel = { create: "Added", update: "Updated", delete: "Deleted" };

const describeChange = (e) => {
  if (e.action === "create") {
    const a = e.after || {};
    return Object.entries(a).filter(([, v]) => v !== "" && v !== 0 && v !== null && typeof v !== "object")
      .map(([k, v]) => `${k}: ${v}`).join(", ") || "empty row";
  }
  if (e.action === "delete") {
    const b = e.before || {};
    return [b.geo, b.source, b.src, b.funnel].filter(Boolean).join(" / ") || "row";
  }
  const b = e.before || {}, a = e.after || {};
  return Object.keys(a).filter((k) => typeof a[k] !== "object").map((k) => `${k}: ${b[k] ?? "—"} → ${a[k]}`).join(", ");
};

const shortDate = (iso) => (iso || "").slice(0, 10);
const shortWhen = (iso) => (iso || "").slice(0, 16).replace("T", " ");
const normalizeFunnel = (name) => (name || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const funnelTokens = (name) => normalizeFunnel(name).split(" ").filter((t) => t.length >= 3);

const collectSuggestions = (...lists) => {
  const seen = new Map();
  for (const list of lists) for (const raw of list) {
    const val = (raw || "").trim();
    if (val) seen.set(val.toLowerCase(), val);
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
};

const csvEscape = (val) => {
  const s = val === null || val === undefined ? "" : String(val);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCsv = (filename, rows) => {
  const content = rows.map((r) => r.map(csvEscape).join(",")).join("\r\n");
  const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
const OFFER_EXPORT_COLUMNS = [
  ["source", "Source"], ["geo", "Geo"], ["src", "Buy SRC"], ["funnel", "Buy Funnel"],
  ["crg_buy", "Buy CRG %"], ["cpa_buy", "Buy CPA"], ["buying_cpl", "Buy CPL"],
  ["src_sell", "Sell SRC"], ["funnel_sell", "Sell Funnel"], ["crg_sell", "Sell CRG %"], ["cpa_sell", "Sell CPA"], ["selling_cpl", "Sell CPL"],
  ["net", "NET"], ["notes", "Notes"], ["updated_by", "Updated By"], ["updated_at", "Updated At"],
];
const numStr = (n) => String(Number(n || 0));

export default function PriceListPage() {
  const [offers, setOffers] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("offers");
  const [search, setSearch] = useState("");
  const [selSource, setSelSource] = useState("");
  const [selGeo, setSelGeo] = useState("");
  const [addingOffer, setAddingOffer] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rowHistory, setRowHistory] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const [plRes, srcRes] = await Promise.all([api.get("/price-list"), api.get("/sources")]);
      setOffers(plRes.data.offers || []);
      setSources(srcRes.data || []);
    } catch (e) {
      toast.error("Failed to load price list");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const suggestions = useMemo(() => ({
    sources: collectSuggestions(sources.map((s) => s.name), offers.map((o) => o.source)),
    src: collectSuggestions(offers.map((o) => o.src), offers.map((o) => o.src_sell)),
    funnels: collectSuggestions(offers.map((o) => o.funnel), offers.map((o) => o.funnel_sell)),
  }), [sources, offers]);

  const sourceOptions = useMemo(
    () => suggestions.sources.map((name) => ({ value: name, label: name, searchText: name, testId: `source-opt-${name}` })),
    [suggestions.sources]
  );
  const geoOptions = useMemo(() => COUNTRIES.map((c) => ({
    value: c.code, label: `${c.code} ${c.name}`, searchText: `${c.code} ${c.name}`, testId: `geo-opt-${c.code}`,
    render: (
      <span className="flex items-center gap-2">
        <span className="text-base leading-none">{flagEmoji(c.code)}</span>
        <span className="font-semibold">{c.code}</span>
        <span className="text-muted-foreground">{c.name}</span>
      </span>
    ),
  })), []);

  const matchOffer = (o) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return ["source", "geo", "src", "funnel", "src_sell", "funnel_sell", "notes"]
      .some((f) => String(o[f] || "").toLowerCase().includes(q)) || countryName(o.geo).toLowerCase().includes(q);
  };
  const visibleOffers = useMemo(
    () => offers.filter((o) => (!selSource || (o.source || "") === selSource) && (!selGeo || (o.geo || "") === selGeo) && matchOffer(o)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offers, selSource, selGeo, search]
  );

  const patchOffer = async (id, payload) => {
    try {
      const res = await api.patch(`/price-list/offers/${id}`, payload);
      setOffers(res.data.offers || []);
    } catch (e) { toast.error("Failed to save. Value reverted."); load(); }
  };
  const resetAuto = async (id, field) => {
    try {
      const res = await api.patch(`/price-list/offers/${id}`, { reset_auto: [field] });
      setOffers(res.data.offers || []);
      toast.info("Reset to auto — re-derived from Buy.");
    } catch (e) { toast.error("Failed to reset."); load(); }
  };
  const addOffer = async () => {
    if (!selSource || !selGeo) { toast.info("Pick a Source and a Geo first."); return; }
    setAddingOffer(true);
    try {
      const res = await api.post("/price-list/offers", { source: selSource, geo: selGeo });
      setOffers(res.data.offers || []);
      toast.success("Offer added — fill the Funnel and Buy prices.");
    } catch (e) { toast.error("Failed to add offer"); } finally { setAddingOffer(false); }
  };
  const createSource = async (name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    try {
      await api.post("/sources", { name: clean });
      const res = await api.get("/sources");
      setSources(res.data || []);
      setSelSource(clean);
      toast.success(`Source “${clean}” created`);
    } catch (e) { toast.error("Failed to create source"); }
  };

  const buildCopyText = (o) => [
    `${flagEmoji(o.geo)} ${(o.geo || "").toUpperCase()}`.trim(),
    `Crg: ${numStr(o.crg_sell)}%`,
    `Cpa: $${numStr(o.cpa_sell)}`,
    `Source: ${o.src_sell || ""}`,
    `Funnel: ${o.funnel_sell || ""}`,
  ].join("\n");
  const copyOffer = async (o) => {
    try { await navigator.clipboard.writeText(buildCopyText(o)); toast.success("Offer copied to clipboard"); }
    catch (e) { toast.error("Copy failed — select and copy manually"); }
  };
  const exportOffers = () => {
    if (visibleOffers.length === 0) { toast.info("No offers to export."); return; }
    const header = OFFER_EXPORT_COLUMNS.map(([, l]) => l);
    const body = visibleOffers.map((o) => OFFER_EXPORT_COLUMNS.map(([k]) => o[k] ?? ""));
    downloadCsv(`price_list_offers_${new Date().toISOString().slice(0, 10)}.csv`, [header, ...body]);
    toast.success(`Exported ${visibleOffers.length} offer(s)`);
  };

  // FUNNELS (offers only)
  const funnelGroups = useMemo(() => {
    const groups = {};
    for (const o of offers) {
      const key = normalizeFunnel(o.funnel);
      if (!key) continue;
      if (!groups[key]) groups[key] = { display: o.funnel, entries: [] };
      groups[key].entries.push({ source: o.source, src: o.src, geo: o.geo, cpa: o.cpa_buy, crg: o.crg_buy, when: o.updated_at });
    }
    const keys = Object.keys(groups);
    for (const k of keys) {
      const mine = new Set(funnelTokens(groups[k].display));
      groups[k].similar = keys.filter((o) => o !== k && (o.includes(k) || k.includes(o) || funnelTokens(groups[o].display).some((t) => mine.has(t))))
        .map((o) => groups[o].display);
    }
    return groups;
  }, [offers]);

  const filteredFunnelKeys = useMemo(() => {
    const q = search.trim().toLowerCase();
    const keys = Object.keys(funnelGroups);
    if (!q) return keys.sort((a, b) => funnelGroups[b].entries.length - funnelGroups[a].entries.length);
    const match = (k) => {
      const g = funnelGroups[k];
      // funnel NAME: word-start match (so "it" does not match "credit")
      if (normalizeFunnel(g.display).split(" ").some((w) => w.startsWith(q))) return true;
      // entries: geo code exact, country name, source, src
      return g.entries.some((e) =>
        (e.geo || "").toLowerCase() === q ||
        countryName(e.geo).toLowerCase().includes(q) ||
        (e.source || "").toLowerCase().includes(q) ||
        (e.src || "").toLowerCase().includes(q));
    };
    return keys.filter(match).sort((a, b) => funnelGroups[b].entries.length - funnelGroups[a].entries.length);
  }, [funnelGroups, search]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await api.delete(`/price-list/offers/${toDelete.row.id}`);
      setOffers(res.data.offers || []);
      toast.success("Offer deleted");
    } catch (e) { toast.error("Failed to delete"); } finally { setToDelete(null); }
  };
  const openHistory = async () => {
    setHistoryOpen(true); setHistoryLoading(true);
    try { const res = await api.get("/price-list/history?limit=100"); setHistoryEntries(res.data.entries || []); }
    catch (e) { toast.error("Failed to load history"); } finally { setHistoryLoading(false); }
  };
  const openRowHistory = async (row, title) => {
    setRowHistory({ title, entries: [], loading: true });
    try { const res = await api.get(`/price-list/row-history/offer/${row.id}`); setRowHistory({ title, entries: res.data.entries || [], loading: false }); }
    catch (e) { toast.error("Failed to load row history"); setRowHistory(null); }
  };

  return (
    <div className="min-h-screen bg-background">
      <TopBar crumbs={[{ label: "Price List" }]} />
      <main className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6" data-testid="price-list-page">
        <datalist id="pl-src">{suggestions.src.map((v) => <option key={v} value={v} />)}</datalist>
        <datalist id="pl-funnels">{suggestions.funnels.map((v) => <option key={v} value={v} />)}</datalist>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Price List</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Offers rate card (buy vs sell with CPL &amp; NET) and funnel comparison. Prices here never affect balances.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="offers" data-testid="price-tab-offers">Offers</TabsTrigger>
                <TabsTrigger value="funnels" data-testid="price-tab-funnels">Funnels</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="price-list-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search source, geo, funnel…" className="h-9 w-60 pl-8" />
            </div>
            <Button variant="outline" size="sm" onClick={openHistory} data-testid="price-list-history-button">
              <History className="mr-1.5 h-4 w-4" /> History
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : tab === "offers" ? (
          <div className="space-y-4" data-testid="offers-panel">
            {/* INDEPENDENT FILTERS + ADD */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Filter &amp; add offers</CardTitle>
                <CardDescription>
                  Filter by <span className="font-semibold">Source</span> and/or <span className="font-semibold">Geo</span> (independent),
                  or use the search box. To add a new offer, pick a Source and a Geo, then press Add Offer — you can add several
                  offers to the same Source+Geo with different funnels.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto_auto]">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Source</label>
                    <Combobox testId="offer-source-combobox" value={selSource} onChange={setSelSource}
                      options={sourceOptions} onCreate={createSource} createLabel={(q) => `Create source “${q}”`}
                      placeholder="All / pick source…" searchPlaceholder="Search or type a new source…"
                      emptyText="No sources — type a name to create one." />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Geo</label>
                    <Combobox testId="offer-geo-combobox" value={selGeo} onChange={setSelGeo}
                      options={geoOptions} placeholder="All / pick country…" searchPlaceholder="Search country or code…"
                      emptyText="No country found." />
                  </div>
                  <div className="flex items-end">
                    <Button className="h-9 w-full lg:w-auto" onClick={addOffer} disabled={addingOffer || !selSource || !selGeo}
                      data-testid="offers-add-button">
                      {addingOffer ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
                      Add Offer
                    </Button>
                  </div>
                  <div className="flex items-end gap-2">
                    {(selSource || selGeo) && (
                      <Button variant="ghost" size="sm" className="h-9" onClick={() => { setSelSource(""); setSelGeo(""); }}
                        data-testid="offers-clear-filters">Clear</Button>
                    )}
                    <Button variant="outline" size="sm" className="h-9" onClick={exportOffers} disabled={visibleOffers.length === 0}
                      data-testid="offers-export-button"><Download className="mr-1.5 h-4 w-4" /> CSV</Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* OFFER CARDS */}
            {visibleOffers.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-testid="offers-empty">
                {offers.length === 0
                  ? "No offers yet. Pick a Source and Geo above, then press Add Offer."
                  : "No offers match the current filters."}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground" data-testid="offers-count">{visibleOffers.length} offer(s)</p>
                {visibleOffers.map((off, idx) => (
                  <Card key={off.id} className="shadow-sm" data-testid={`offer-card-${idx}`}>
                    <CardHeader className="flex flex-col gap-2 pb-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                          <span className="text-xl leading-none">{flagEmoji(off.geo)}</span>
                          <span className="font-mono">{off.geo || "—"}</span>
                          <span className="text-muted-foreground">·</span>
                          <span>{off.source || "—"}</span>
                          {off.funnel ? <span className="rounded-full border border-primary/30 bg-accent/40 px-2 py-0.5 text-xs font-medium">{off.funnel}</span> : null}
                        </CardTitle>
                        <CardDescription>
                          {countryName(off.geo)} — Buy CPL {fmtCurrency(off.buying_cpl || 0)} · Sell CPL {fmtCurrency(off.selling_cpl || 0)}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => copyOffer(off)} data-testid={`offer-card-${idx}-copy`}>
                          <Copy className="mr-1.5 h-4 w-4" /> Copy
                        </Button>
                        <Button variant="outline" size="icon" className="h-9 w-9" title="Row history" data-testid={`offer-card-${idx}-history`}
                          onClick={() => openRowHistory(off, `OFFER — ${[off.source, off.geo, off.funnel].filter(Boolean).join(" / ") || "row"}`)}>
                          <Clock className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="icon" className="h-9 w-9 text-muted-foreground hover:text-red-600" title="Delete offer"
                          data-testid={`offer-card-${idx}-delete`} onClick={() => setToDelete({ row: off })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 lg:grid-cols-2">
                        <div className="rounded-lg border border-emerald-600/30 bg-emerald-50/40 p-3">
                          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-emerald-700">Buy — what you pay</p>
                          <div className="space-y-2">
                            <EditRow label="SRC (traffic source)"><Cell value={off.src} testId={`offer-card-${idx}-buy-src`} placeholder="GG+FB / Taboola" listId="pl-src" onCommit={(v) => patchOffer(off.id, { src: v })} /></EditRow>
                            <EditRow label="Funnel"><Cell value={off.funnel} testId={`offer-card-${idx}-buy-funnel`} placeholder="Funnel name" listId="pl-funnels" onCommit={(v) => patchOffer(off.id, { funnel: v })} /></EditRow>
                            <EditRow label="CRG %"><Cell value={off.crg_buy} numeric testId={`offer-card-${idx}-buy-crg`} onCommit={(v) => patchOffer(off.id, { crg_buy: v })} /></EditRow>
                            <EditRow label="CPA $"><Cell value={off.cpa_buy} numeric testId={`offer-card-${idx}-buy-cpa`} onCommit={(v) => patchOffer(off.id, { cpa_buy: v })} /></EditRow>
                          </div>
                          <div className="mt-3 flex items-center justify-between border-t border-emerald-600/20 pt-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Buying CPL</span>
                            <ComputedCell value={off.buying_cpl} testId={`offer-card-${idx}-buycpl`} />
                          </div>
                        </div>
                        <div className="rounded-lg border border-primary/30 bg-accent/30 p-3">
                          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-primary">Sell — what you charge</p>
                          <div className="space-y-2">
                            <EditRow label="SRC"><SellField value={off.src_sell} isAuto={off.auto?.src_sell} listId="pl-src" testId={`offer-card-${idx}-sell-src`} onCommit={(v) => patchOffer(off.id, { src_sell: v })} onReset={() => resetAuto(off.id, "src_sell")} /></EditRow>
                            <EditRow label="Funnel"><SellField value={off.funnel_sell} isAuto={off.auto?.funnel_sell} listId="pl-funnels" testId={`offer-card-${idx}-sell-funnel`} onCommit={(v) => patchOffer(off.id, { funnel_sell: v })} onReset={() => resetAuto(off.id, "funnel_sell")} /></EditRow>
                            <EditRow label="CRG %"><SellField value={off.crg_sell} isAuto={off.auto?.crg_sell} numeric testId={`offer-card-${idx}-sell-crg`} onCommit={(v) => patchOffer(off.id, { crg_sell: v })} onReset={() => resetAuto(off.id, "crg_sell")} /></EditRow>
                            <EditRow label="CPA $"><SellField value={off.cpa_sell} isAuto={off.auto?.cpa_sell} numeric testId={`offer-card-${idx}-sell-cpa`} onCommit={(v) => patchOffer(off.id, { cpa_sell: v })} onReset={() => resetAuto(off.id, "cpa_sell")} /></EditRow>
                          </div>
                          <div className="mt-3 flex items-center justify-between border-t border-primary/20 pt-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Selling CPL</span>
                            <ComputedCell value={off.selling_cpl} testId={`offer-card-${idx}-sellcpl`} />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-3 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">NET</span>
                          <NetCell value={off.net} testId={`offer-card-${idx}-net`} />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Copy preview</span>
                          <pre data-testid={`offer-card-${idx}-copy-preview`} className="max-w-full overflow-x-auto rounded-md border border-border bg-background px-3 py-2 text-xs leading-relaxed">{buildCopyText(off)}</pre>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* FUNNELS TAB */
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base"><Filter className="h-4 w-4 text-primary" /> Funnels — Compare Across Sources</CardTitle>
              <CardDescription>
                Every funnel from your offers, grouped by name. Search by funnel name, Geo code (e.g. IT), country (e.g. Italy) or Source.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredFunnelKeys.length === 0 ? (
                <p className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground" data-testid="funnels-empty">
                  {search ? "No funnels match your search." : "No funnels yet. Fill the Funnel field on offers to build this view."}
                </p>
              ) : (
                <div className="space-y-4">
                  {filteredFunnelKeys.map((key, gi) => {
                    const g = funnelGroups[key];
                    const sorted = [...g.entries].sort((a, b) => (a.cpa || 1e12) - (b.cpa || 1e12));
                    const cheapest = sorted.find((e) => e.cpa > 0);
                    return (
                      <div key={key} data-testid={`funnel-group-${gi}`} className="rounded-lg border border-border p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-bold">{g.display}</p>
                          <span className="text-[11px] text-muted-foreground">
                            {g.entries.length} entr{g.entries.length === 1 ? "y" : "ies"}
                            {g.similar && g.similar.length > 0 && <> · Similar: {[...new Set(g.similar)].slice(0, 3).join(", ")}</>}
                          </span>
                        </div>
                        <div className="mt-2 w-full overflow-x-auto">
                          <Table className="min-w-[620px]">
                            <TableHeader>
                              <TableRow className="bg-muted/40">
                                <TableHead className="w-40">Source</TableHead>
                                <TableHead className="w-28">SRC</TableHead>
                                <TableHead className="w-20">Geo</TableHead>
                                <TableHead className="w-24 text-right">Buy CPA</TableHead>
                                <TableHead className="w-20 text-right">Buy CRG</TableHead>
                                <TableHead className="w-28">When</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {sorted.map((e, ei) => (
                                <TableRow key={ei}>
                                  <TableCell className="text-sm font-medium">{e.source || "—"}</TableCell>
                                  <TableCell className="text-xs text-muted-foreground">{e.src || "—"}</TableCell>
                                  <TableCell className="text-sm"><span className="mr-1">{flagEmoji(e.geo)}</span>{e.geo || "—"}</TableCell>
                                  <TableCell className={cn("text-right font-mono text-sm tabular-nums", cheapest && e === cheapest && "font-bold text-emerald-600")}>
                                    {e.cpa > 0 ? fmtCurrency(e.cpa) : "—"}{cheapest && e === cheapest && <span className="ml-1 text-[10px] font-normal">best</span>}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-sm tabular-nums">{e.crg > 0 ? `${e.crg}%` : "—"}</TableCell>
                                  <TableCell className="text-xs text-muted-foreground">{shortDate(e.when)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* GLOBAL HISTORY */}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent data-testid="price-list-history-dialog" className="max-h-[80vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Price List — Change History</DialogTitle>
              <DialogDescription>Every change is recorded: who, what and when. Most recent first.</DialogDescription>
            </DialogHeader>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>
            ) : historyEntries.length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">No changes recorded yet.</p>
            ) : (
              <div className="space-y-2">
                {historyEntries.map((e, idx) => (
                  <div key={e.id} data-testid={`price-history-entry-${idx}`} className="rounded-md border border-border p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold">{actionLabel[e.action] || e.action} {String(e.entity || "").toUpperCase()} row</span>
                      <span className="text-[11px] text-muted-foreground">{shortWhen(e.created_at)} · {e.user}</span>
                    </div>
                    <p className="mt-1 break-words text-xs text-foreground">{describeChange(e)}</p>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ROW TIMELINE */}
        <Dialog open={!!rowHistory} onOpenChange={(v) => !v && setRowHistory(null)}>
          <DialogContent data-testid="price-row-history-dialog" className="max-h-[80vh] max-w-xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Row Timeline</DialogTitle>
              <DialogDescription>{rowHistory?.title}</DialogDescription>
            </DialogHeader>
            {rowHistory?.loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>
            ) : (rowHistory?.entries || []).length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">No changes recorded for this row yet.</p>
            ) : (
              <div className="space-y-0">
                {(rowHistory?.entries || []).map((e, idx) => (
                  <div key={e.id} data-testid={`row-history-entry-${idx}`} className="relative border-l-2 border-primary/30 pb-3 pl-4 last:pb-0">
                    <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-primary" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold">{actionLabel[e.action] || e.action}</span>
                      <span className="text-[11px] text-muted-foreground">{shortWhen(e.created_at)} · {e.user}</span>
                    </div>
                    <p className="mt-0.5 break-words text-xs text-foreground">{describeChange(e)}</p>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* DELETE CONFIRM */}
        <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
          <AlertDialogContent data-testid="price-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this offer?</AlertDialogTitle>
              <AlertDialogDescription>
                {toDelete ? `${[toDelete.row.geo, toDelete.row.source, toDelete.row.funnel].filter(Boolean).join(" / ") || "empty row"}. The deletion will be recorded in the history.` : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="price-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="price-delete-confirm" onClick={confirmDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
